import {
	Component,
	OnInit,
	Input,
	ViewChild,
	ContentChild,
	OnDestroy,
	Directive,
	ElementRef,
	HostListener,
	Output,
	EventEmitter,
	AfterViewInit,
} from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { DataSetComponent } from './data-set.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Directive({ selector: '[appDataSetRowClick]' })
export class DataSetRowClickDirective implements OnInit, OnDestroy {
	@Output() appDataSetRowClick = new EventEmitter();

	private clicks = new Subject();
	private subscription?: Subscription;

	constructor(private el: ElementRef) {}

	@HostListener('click', ['$event']) onClick(event: any): void {
		event.preventDefault();
		event.stopPropagation();
		this.clicks.next(event);
	}

	ngOnInit(): void {
		this.subscription = this.clicks
			.pipe(
				tap(() => {
					this.el.nativeElement.singleClicking = true;
				}),
				debounceTime(250),
				filter((_) => this.el.nativeElement.singleClicking)
			)
			.subscribe((e) => {
				this.el.nativeElement.singleClicking = false;
				this.appDataSetRowClick.emit(e);
			});
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
		this.subscription = undefined;
	}
}

@Directive({ selector: '[appDataSetRowDoubleClick]' })
export class DataSetRowDoubleClickDirective {
	@Output() appDataSetRowDoubleClick = new EventEmitter();

	constructor(private el: ElementRef) {}

	@HostListener('dblclick', ['$event']) onClick(event: any): void {
		this.el.nativeElement.singleClicking = false;

		event.preventDefault();
		event.stopPropagation();
		this.appDataSetRowDoubleClick.emit(event);
	}
}

@Component({
	selector: 'app-data-set-table-row-selection',
	template: `
		<mat-checkbox
			data-automation-id="data-set-row-selection-checkbox"
			(click)="$event.stopPropagation()"
			(change)="toggleSelected()"
			[checked]="selected"
		>
		</mat-checkbox>
	`,
	styleUrls: ['./data-set.component.scss'],
	imports: [MatCheckbox],
})
export class DataSetTableRowSelectionComponent implements OnInit, OnDestroy {
	@Input() data!: any;

	selected = false;

	private selectionSubscription?: Subscription;

	constructor(readonly table: DataSetTableComponent) {}

	ngOnInit(): void {
		this.selected = this.table.dataSet.selection.isSelected(this.data);
		this.selectionSubscription = this.table.dataSet.selection.changed.subscribe(
			(changeEvent) => {
				changeEvent.added.forEach((added) => {
					if (
						this.table.dataSet.rowId(added) ===
						this.table.dataSet.rowId(this.data)
					) {
						this.selected = true;
					}
				});

				changeEvent.removed.forEach((removed) => {
					if (
						this.table.dataSet.rowId(removed) ===
						this.table.dataSet.rowId(this.data)
					) {
						this.selected = false;
					}
				});
			}
		);
	}

	ngOnDestroy(): void {
		this.selectionSubscription?.unsubscribe();
		this.selectionSubscription = undefined;
	}

	toggleSelected(): void {
		this.table.dataSet.selection.toggle(this.data);
	}
}

@Component({
	selector: 'app-data-set-table-menu',
	template: `
		<button
			mat-icon-button
			[matMenuTriggerFor]="menu"
			[matMenuTriggerData]="{ row: row }"
			(click)="$event.stopPropagation()"
			data-automation-id="data-set-more-button"
		>
			<mat-icon>more_horizontal</mat-icon>
		</button>
	`,
	styleUrls: ['./data-set.component.scss'],
	imports: [MatIconButton, MatMenuTrigger, MatIcon],
})
export class DataSetTableMenuComponent {
	@Input() menu!: MatMenu;
	@Input() row!: any;

	constructor() {}
}

export type DataSetTablePresentationFilters = {
	rowIndex: number;
	rowCount?: number;
	sortBy?: string;
	sortDirection?: string;
};

@Component({
	selector: 'app-data-set-table-view',
	template: ` @if (displayed) {
		<div class="data-set-table-view data-set-table-content">
			<ng-content></ng-content>
		</div>
		@if (!hidePaginator) {
			<div class="data-set-table-view-footer">
				<mat-paginator
					#paginator
					[pageSizeOptions]="pageSizeOptions"
					data-automation-id="data-set-paginator"
				></mat-paginator>
			</div>
		}
	}`,
	styleUrls: ['./data-set.component.scss'],
	imports: [MatPaginator],
})
export class DataSetTableComponent implements AfterViewInit, OnDestroy {
	pageSizeOptions = [30, 50, 75];
	@Input() fetchAllResults = false;
	@Input() hidePaginator = false;

	@ContentChild(MatTable, { read: MatTable }) table!: MatTable<any>;
	@ContentChild(MatSort) sort!: MatSort;
	@ViewChild(MatPaginator) paginator!: MatPaginator;

	displayed = false;

	private presentationSubscription?: Subscription;
	private paginationSubscription?: Subscription;
	private sortSubscription?: Subscription;

	constructor(readonly dataSet: DataSetComponent) {
		this.presentationSubscription = this.dataSet.presentationMode.subscribe(
			(mode) => {
				this.displayed = mode === 'table';
			}
		);
	}

	ngOnDestroy(): void {
		this.presentationSubscription?.unsubscribe();
		this.presentationSubscription = undefined;

		this.paginationSubscription?.unsubscribe();
		this.paginationSubscription = undefined;

		this.sortSubscription?.unsubscribe();
		this.sortSubscription = undefined;
	}

	ngAfterViewInit(): void {
		const dataSource = this.table.dataSource as MatTableDataSource<any>;
		dataSource.paginator = this.paginator;
		dataSource.sort = this.sort;

		this.updatePresentationFilters();

		this.paginationSubscription = this.paginator.page.subscribe((_: any) => {
			if (!this.fetchAllResults) {
				// if we're not fetching all the results up front then we have to update the filters
				// so we refetch all the data with the next page worth of data included
				this.updatePresentationFilters();
			}
		});

		this.sortSubscription = this.sort.sortChange.subscribe((_) => {
			this.updatePresentationFilters();
		});
	}

	private updatePresentationFilters(): void {
		this.dataSet.updatePresentationFilters(this.getCurrentFilters());
	}

	private getCurrentFilters(): DataSetTablePresentationFilters {
		const index = this.paginator.pageIndex * this.paginator.pageSize;

		let filters: DataSetTablePresentationFilters;

		if (!this.fetchAllResults) {
			// if we're not fetching all the results up front then we have to include the rowCount
			// so we refetch all the data with the next page worth of data included
			filters = {
				// Paginator expects that it is paging through all data.
				// This fetches more than what is showing but paginator expects we have everything.
				rowIndex: 0,
				rowCount: index + this.paginator.pageSize + 1, // include one extra so there is always a 'next page'
			};
		} else {
			// we're fetching all the data upfront so we don't include the rowCount here
			filters = {
				// Paginator expects that it is paging through all data.
				// This fetches more than what is showing but paginator expects we have everything.
				rowIndex: 0,
			};
		}

		if (this.sort.direction !== '') {
			filters.sortBy = this.sort.active;
			filters.sortDirection = this.sort.direction;
		}

		return filters;
	}
}

@Component({
	selector: 'app-data-set-infinite-scroll-view',
	template: ` @if (displayed) {
		<div class="data-set-table-view data-set-table-content">
			<ng-content></ng-content>
		</div>
	}`,
	styleUrls: ['./data-set.component.scss'],
})
export class DataSetInfiniteScrollViewComponent
	implements AfterViewInit, OnDestroy
{
	@ContentChild(MatTable, { read: MatTable }) table!: MatTable<any>;
	@ContentChild(MatSort) sort!: MatSort;

	displayed = false;

	private presentationSubscription?: Subscription;
	private sortSubscription?: Subscription;

	constructor(readonly dataSet: DataSetComponent) {
		this.presentationSubscription = this.dataSet.presentationMode.subscribe(
			(mode) => {
				this.displayed = mode === 'table';
			}
		);
	}

	ngOnDestroy(): void {
		this.presentationSubscription?.unsubscribe();
		this.presentationSubscription = undefined;

		this.sortSubscription?.unsubscribe();
		this.sortSubscription = undefined;
	}

	ngAfterViewInit(): void {
		const dataSource = this.table.dataSource as MatTableDataSource<any>;
		dataSource.sort = this.sort;

		this.updatePresentationFilters();

		this.sortSubscription = this.sort.sortChange.subscribe((_) => {
			this.updatePresentationFilters();
		});
	}

	private updatePresentationFilters(): void {
		this.dataSet.updatePresentationFilters(this.getCurrentFilters());
	}

	private getCurrentFilters(): DataSetTablePresentationFilters {
		const filters: DataSetTablePresentationFilters = {
			rowIndex: 0,
		};

		if (this.sort.direction !== '') {
			filters.sortBy = this.sort.active;
			filters.sortDirection = this.sort.direction;
		}

		return filters;
	}
}

//* Added the following component to provide a table view without a paginator - currently used in List of Contact :
@Component({
	selector: 'app-data-set-table-view-without-paginator',
	template: ` @if (displayed) {
		<div class="data-set-table-view data-set-table-content">
			<ng-content></ng-content>
		</div>
		<div class="data-set-table-view-footer"></div>
	}`,
	styleUrls: ['./data-set.component.scss'],
})
export class DataSetTableWithoutPaginatorComponent
	implements AfterViewInit, OnDestroy
{
	@Input() fetchAllResults = false;

	@ContentChild(MatTable, { read: MatTable }) table!: MatTable<any>;
	@ContentChild(MatSort) sort!: MatSort;

	displayed = false;

	private presentationSubscription?: Subscription;
	private paginationSubscription?: Subscription;
	private sortSubscription?: Subscription;

	constructor(readonly dataSet: DataSetComponent) {
		this.presentationSubscription = this.dataSet.presentationMode.subscribe(
			(mode) => {
				this.displayed = mode === 'table';
			}
		);
	}

	ngOnDestroy(): void {
		this.presentationSubscription?.unsubscribe();
		this.presentationSubscription = undefined;

		this.paginationSubscription?.unsubscribe();
		this.paginationSubscription = undefined;

		this.sortSubscription?.unsubscribe();
		this.sortSubscription = undefined;
	}

	ngAfterViewInit(): void {
		if (this.table?.dataSource) {
			const dataSource = this.table?.dataSource as MatTableDataSource<any>;
			dataSource.sort = this.sort;

			this.updatePresentationFilters();

			this.sortSubscription = this.sort.sortChange.subscribe((_) => {
				this.updatePresentationFilters();
			});
		}
	}

	private updatePresentationFilters(): void {
		this.dataSet.updatePresentationFilters(this.getCurrentFilters());
	}

	private getCurrentFilters(): DataSetTablePresentationFilters {
		let filters: DataSetTablePresentationFilters;

		if (!this.fetchAllResults) {
			filters = {
				// Paginator expects that it is paging through all data.
				// This fetches more than what is showing but paginator expects we have everything.
				rowIndex: 0,
			};
		} else {
			filters = {
				// Paginator expects that it is paging through all data.
				// This fetches more than what is showing but paginator expects we have everything.
				rowIndex: 0,
			};
		}

		if (this.sort.direction !== '') {
			filters.sortBy = this.sort.active;
			filters.sortDirection = this.sort.direction;
		}

		return filters;
	}
}
